import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import api, { formatCurrency } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Tag, Sparkles } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "travel",    label: "Travel" },
  { value: "money",     label: "Money" },
  { value: "crypto",    label: "Crypto" },
  { value: "shopping",  label: "Shopping" },
  { value: "mobility",  label: "Mobility" },
  { value: "esim",      label: "eSIMs" },
  { value: "insurance", label: "Insurance" },
  { value: "lifestyle", label: "Lifestyle" },
];

const blank = {
  name: "", slug: "", category: "travel", blurb: "", description: "",
  referral_url: "", logo_url: "", hero_color: "#0055FF",
  commission_rate: "", featured: false, active: true,
};

const LifestyleAdminPage = () => {
  const [tab, setTab] = useState("partners");
  const [partners, setPartners] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [importing, setImporting] = useState(false);

  const loadPartners = async () => {
    const r = await api.get("/lifestyle/admin/partners");
    setPartners(r.data.partners || []);
  };
  const loadCommissions = async () => {
    const r = await api.get("/lifestyle/admin/commissions");
    setCommissions(r.data.commissions || []);
  };

  useEffect(() => { loadPartners(); loadCommissions(); }, []);

  const openNew = () => { setEditing("new"); setForm(blank); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...blank, ...p }); };
  const close = () => { setEditing(null); setForm(blank); };

  const save = async () => {
    if (!form.name || !form.slug || !form.referral_url) {
      toast.error("Name, slug, and referral URL are required");
      return;
    }
    try {
      if (editing === "new") {
        await api.post("/lifestyle/admin/partners", form);
        toast.success("Partner created");
      } else {
        await api.put(`/lifestyle/admin/partners/${editing}`, form);
        toast.success("Partner updated");
      }
      close();
      loadPartners();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this partner? Past commissions will remain.")) return;
    try {
      await api.delete(`/lifestyle/admin/partners/${id}`);
      toast.success("Partner deleted");
      loadPartners();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const handleConfirm = async (id) => {
    try {
      await api.post(`/lifestyle/admin/commissions/${id}/confirm`);
      toast.success("Commission confirmed");
      loadCommissions();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const handleMarkPaid = async (id) => {
    try {
      await api.post(`/lifestyle/admin/commissions/${id}/mark-paid`);
      toast.success("Marked paid");
      loadCommissions();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/lifestyle/admin/commissions/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${r.data.imported} rows · ${r.data.errors.length} errors`);
      if (r.data.errors.length) console.warn("Import errors:", r.data.errors);
      loadCommissions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="lifestyle-admin-page">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="text-blue-600" size={16} />
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Affiliate management
            </span>
          </div>
          <h1 className="text-3xl font-serif tracking-tight text-slate-900">Lifestyle Products</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-3xl">
            Manage your affiliate-partner roster. Edit referral URLs, commission rates, featured slots,
            and import monthly conversion CSVs from each network. Tracking-only mode — payouts happen
            off-portal until Paytiko is live.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="partners" data-testid="admin-tab-partners">Partners ({partners.length})</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="admin-tab-commissions">Commissions ({commissions.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="partners" className="mt-5 space-y-4">
            <div className="flex justify-end">
              <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-testid="add-partner-btn">
                <Plus size={14} className="mr-1" /> Add partner
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {partners.map((p) => (
                <Card key={p.id} className="bg-white border-slate-200" data-testid={`admin-partner-${p.slug}`}>
                  <div className="h-14" style={{ background: `linear-gradient(135deg, ${p.hero_color || "#0055FF"}, #10B981)` }} />
                  <CardContent className="p-4 -mt-7">
                    <div className="flex items-start gap-3">
                      <div className="h-12 w-12 rounded-lg bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                        {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-9 w-9 object-contain" /> : <span className="font-semibold">{p.name[0]}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-slate-900 truncate">{p.name}</h3>
                        <p className="text-xs text-slate-500">{p.slug} · {p.category}</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.blurb || p.description}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      {p.commission_rate && <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 text-xs"><Tag size={10} className="mr-1" />{p.commission_rate}</Badge>}
                      {p.featured && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">FEATURED</Badge>}
                      {!p.active && <Badge className="bg-slate-100 text-slate-600 text-xs">INACTIVE</Badge>}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button onClick={() => openEdit(p)} variant="outline" size="sm" className="flex-1"><Pencil size={12} className="mr-1" /> Edit</Button>
                      <Button onClick={() => remove(p.id)} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50"><Trash2 size={12} /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="commissions" className="mt-5 space-y-3">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Import monthly CSV</CardTitle>
                <CardDescription>
                  CSV columns: <code>user_email, partner_slug, amount, currency (optional), notes (optional)</code>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="file" accept=".csv" onChange={importCsv} hidden disabled={importing} data-testid="import-csv-input" />
                  <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
                    <span><Upload size={14} className="mr-1" /> {importing ? "Importing…" : "Upload CSV"}</span>
                  </Button>
                </label>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Partner</th>
                      <th className="text-right p-3">Amount</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.length === 0 ? (
                      <tr><td colSpan="5" className="text-center text-slate-400 py-10">No commissions yet — import a CSV above.</td></tr>
                    ) : commissions.map((c) => (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`commission-row-${c.id}`}>
                        <td className="p-3">{c.user_email}</td>
                        <td className="p-3">{c.partner_name} <span className="text-slate-400 text-xs">· {c.partner_category}</span></td>
                        <td className="p-3 text-right font-semibold tabular-nums">{formatCurrency(c.amount)}</td>
                        <td className="p-3"><StatusPill s={c.status} /></td>
                        <td className="p-3 text-right">
                          {c.status === "pending" && <Button onClick={() => handleConfirm(c.id)} size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">Confirm</Button>}
                          {c.status === "confirmed" && <Button onClick={() => handleMarkPaid(c.id)} size="sm" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">Mark paid</Button>}
                          {c.status === "paid" && <span className="text-xs text-slate-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
          <DialogContent className="bg-white max-w-lg" data-testid="partner-dialog">
            <DialogHeader><DialogTitle>{editing === "new" ? "Add partner" : "Edit partner"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-name" /></div>
              <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} disabled={editing !== "new"} data-testid="partner-slug" /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-surface border-platinum/20 text-platinum"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORY_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Blurb (1 line)</Label><Input value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} /></div>
              <div className="col-span-2"><Label>Referral URL</Label><Input value={form.referral_url} onChange={(e) => setForm({ ...form, referral_url: e.target.value })} placeholder="https://..." data-testid="partner-url" /></div>
              <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} /></div>
              <div><Label>Hero color</Label><Input value={form.hero_color} onChange={(e) => setForm({ ...form, hero_color: e.target.value })} placeholder="#0055FF" /></div>
              <div><Label>Commission rate</Label><Input value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} placeholder="e.g. 5% or $10/signup" /></div>
              <div className="col-span-2"><Label>Description (longer)</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-testid="save-partner-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

const StatusPill = ({ s }) => {
  const cls = s === "paid" ? "bg-blue-50 text-blue-700 border-blue-200"
    : s === "confirmed" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-amber-50 text-amber-700 border-amber-200";
  return <Badge className={cls}>{s}</Badge>;
};

export default LifestyleAdminPage;
